import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'

/**
 * SelectorColor — elige el color con el que se identifica algo (un número, un
 * negocio, una etiqueta).
 *
 * La idea es no abrumar: a la vista quedan unos pocos colores bien
 * diferenciables, y quien quiera algo específico abre el panel completo, con
 * una paleta amplia y un selector libre para cualquier color.
 *
 * En móvil el panel sube como hoja desde abajo, con objetivos grandes para el
 * dedo; en escritorio aparece como popover junto al botón.
 *
 * La hoja de móvil se monta con un portal en el body a propósito: los modales
 * de la app se animan con `transform`, y un ancestro transformado rompe el
 * `position: fixed` (deja de referirse a la pantalla). Sin el portal, la hoja
 * quedaba colgando fuera del borde inferior.
 */

// Los que se ven sin abrir nada: uno por familia, fáciles de distinguir entre sí
export const COLORES_RAPIDOS = [
  '#25D366', // verde WhatsApp
  '#0ea5e9', // celeste
  '#3b82f6', // azul
  '#8b5cf6', // violeta
  '#ec4899', // rosa
  '#ef4444', // rojo
  '#f97316', // naranja
  '#eab308', // amarillo
  '#14b8a6', // turquesa
  '#64748b', // gris
]

// Paleta completa del panel, por familias y tonos
const FAMILIAS = [
  { nombre: 'Verdes',    tonos: ['#dcfce7', '#86efac', '#22c55e', '#16a34a', '#15803d', '#25D366', '#128C7E', '#075E54'] },
  { nombre: 'Turquesas', tonos: ['#ccfbf1', '#5eead4', '#14b8a6', '#0d9488', '#0f766e', '#67e8f9', '#06b6d4', '#0891b2'] },
  { nombre: 'Azules',    tonos: ['#dbeafe', '#93c5fd', '#3b82f6', '#2563eb', '#1d4ed8', '#7dd3fc', '#0ea5e9', '#0369a1'] },
  { nombre: 'Violetas',  tonos: ['#ede9fe', '#c4b5fd', '#8b5cf6', '#7c3aed', '#6d28d9', '#a5b4fc', '#6366f1', '#4338ca'] },
  { nombre: 'Rosas',     tonos: ['#fce7f3', '#f9a8d4', '#ec4899', '#db2777', '#be185d', '#f0abfc', '#d946ef', '#a21caf'] },
  { nombre: 'Rojos',     tonos: ['#fee2e2', '#fca5a5', '#ef4444', '#dc2626', '#b91c1c', '#fda4af', '#f43f5e', '#9f1239'] },
  { nombre: 'Naranjas',  tonos: ['#ffedd5', '#fdba74', '#f97316', '#ea580c', '#c2410c', '#fcd34d', '#f59e0b', '#b45309'] },
  { nombre: 'Amarillos', tonos: ['#fef9c3', '#fde047', '#eab308', '#ca8a04', '#a16207', '#d9f99d', '#84cc16', '#4d7c0f'] },
  { nombre: 'Neutros',   tonos: ['#f8fafc', '#cbd5e1', '#94a3b8', '#64748b', '#475569', '#334155', '#1e293b', '#0f172a'] },
]

function esClaro(hex) {
  const h = String(hex || '').replace('#', '')
  if (h.length !== 6) return false
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  // Luminancia percibida: decide si la marca de "elegido" va oscura o clara
  return (0.299 * r + 0.587 * g + 0.114 * b) > 165
}

function normalizarHex(valor) {
  let v = String(valor || '').trim()
  if (v && !v.startsWith('#')) v = '#' + v
  return /^#[0-9a-fA-F]{6}$/.test(v) ? v.toLowerCase() : null
}

function Muestra({ color, activo, onClick, tam = 34 }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={color}
      aria-label={color}
      aria-pressed={activo}
      style={{
        width: tam, height: tam, borderRadius: 9, background: color, cursor: 'pointer',
        border: activo ? '2px solid var(--text)' : '1px solid rgba(0,0,0,0.12)',
        boxShadow: activo ? '0 0 0 2px var(--bg-card), 0 0 0 4px var(--text)' : 'var(--shadow-sm)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 0, flexShrink: 0, transition: 'transform .12s',
      }}
    >
      {activo && (
        <svg width={tam * 0.5} height={tam * 0.5} viewBox="0 0 24 24" fill="none"
          stroke={esClaro(color) ? '#111827' : '#ffffff'} strokeWidth="3.5"
          strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      )}
    </button>
  )
}

const ALTO_PANEL = 420   // alto máximo del popover en escritorio
const ANCHO_PANEL = 330
const MARGEN_BORDE = 12  // aire mínimo entre el panel y el borde de la pantalla
const SEPARACION = 8     // hueco entre el botón y el panel
const ALTO_MINIMO = 200  // por debajo de esto el panel deja de ser usable

export default function SelectorColor({ value, onChange, rapidos = COLORES_RAPIDOS }) {
  const [abierto, setAbierto] = useState(false)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768)
  const [pos, setPos] = useState({ top: 0, left: 0, maxHeight: ALTO_PANEL })
  const [hex, setHex] = useState(value || '')
  const contenedor = useRef(null)
  const disparador = useRef(null)
  const panelRef = useRef(null)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => { setHex(value || '') }, [value])

  // Cerrar al hacer clic fuera (solo escritorio: en móvil hay overlay propio).
  // El panel vive en un portal fuera del contenedor, así que hay que excluirlo
  // aparte o cualquier clic dentro de él lo cerraría.
  useEffect(() => {
    if (!abierto || isMobile) return
    const fuera = (e) => {
      if (panelRef.current?.contains(e.target)) return
      if (contenedor.current && !contenedor.current.contains(e.target)) setAbierto(false)
    }
    document.addEventListener('mousedown', fuera)
    return () => document.removeEventListener('mousedown', fuera)
  }, [abierto, isMobile])

  useEffect(() => {
    if (!abierto) return
    const esc = (e) => { if (e.key === 'Escape') setAbierto(false) }
    document.addEventListener('keydown', esc)
    return () => document.removeEventListener('keydown', esc)
  }, [abierto])

  const elegir = (c) => { onChange(c); setAbierto(false) }

  const alternar = () => setAbierto(a => !a)

  /**
   * Coloca el panel de escritorio respecto al botón, en coordenadas de pantalla.
   *
   * El selector casi siempre vive dentro de un modal o de un formulario con
   * scroll propio. Colgar el panel del contenedor con `position:absolute` lo
   * ataba a ese scroll: al abrirlo, el cuerpo del modal se desplazaba y el panel
   * se iba con él, quedando fuera de la pantalla por abajo. Por eso se saca a un
   * portal en <body> con `position:fixed` y se sitúa a mano:
   *
   *  - se abre hacia el lado con más espacio;
   *  - si ni ese lado da para los 420px, se recorta a lo que hay (el panel ya
   *    tiene scroll interno, así que no se pierde ningún color);
   *  - se ajusta también en horizontal, para que no se salga por la derecha
   *    cuando el selector está pegado al borde.
   */
  const colocar = useCallback(() => {
    const boton = disparador.current
    if (!boton) return
    const caja = boton.getBoundingClientRect()
    const abajo = window.innerHeight - caja.bottom - SEPARACION - MARGEN_BORDE
    const arriba = caja.top - SEPARACION - MARGEN_BORDE
    const sube = abajo < ALTO_PANEL && arriba > abajo
    // El alto nunca puede pasar de lo que da la pantalla: si el botón está
    // medio tapado por el borde (lista larga a medio desplazar), el lado
    // elegido puede dar menos que el mínimo, y entonces manda la pantalla.
    const alto = Math.min(
      Math.max(ALTO_MINIMO, Math.min(ALTO_PANEL, sube ? arriba : abajo)),
      window.innerHeight - MARGEN_BORDE * 2,
    )
    // Se ancla al botón y después se empuja dentro de la pantalla, así el panel
    // siempre se ve entero aunque su disparador esté justo en el borde.
    const anclado = sube ? caja.top - SEPARACION - alto : caja.bottom + SEPARACION
    const top = Math.min(
      Math.max(MARGEN_BORDE, anclado),
      window.innerHeight - alto - MARGEN_BORDE,
    )
    const left = Math.min(
      Math.max(MARGEN_BORDE, caja.left),
      window.innerWidth - ANCHO_PANEL - MARGEN_BORDE,
    )
    setPos({ top, left: Math.max(MARGEN_BORDE, left), maxHeight: alto })
  }, [])

  // Se sitúa antes de pintar (para que no se vea saltar) y se recalcula si algo
  // se mueve por debajo: scroll de cualquier ancestro (de ahí la captura) o
  // cambio de tamaño de la ventana.
  useLayoutEffect(() => {
    if (!abierto || isMobile) return
    colocar()
    window.addEventListener('scroll', colocar, true)
    window.addEventListener('resize', colocar)
    return () => {
      window.removeEventListener('scroll', colocar, true)
      window.removeEventListener('resize', colocar)
    }
  }, [abierto, isMobile, colocar])

  // Si el color actual no está entre los rápidos, se muestra igual para que no
  // parezca que no hay nada elegido.
  const actual = normalizarHex(value)
  const enRapidos = actual && rapidos.some(c => c.toLowerCase() === actual)
  const visibles = enRapidos || !actual ? rapidos : [actual, ...rapidos].slice(0, rapidos.length + 1)

  const panel = (
    <div
      ref={panelRef}
      style={isMobile ? {
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1001,
        background: 'var(--bg-card)', borderTopLeftRadius: 18, borderTopRightRadius: 18,
        padding: '10px 16px calc(20px + env(safe-area-inset-bottom))',
        boxShadow: '0 -8px 30px rgba(0,0,0,0.18)', maxHeight: '75vh', overflowY: 'auto',
      } : {
        position: 'fixed', top: pos.top, left: pos.left, zIndex: 1100,
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
        padding: 16, width: ANCHO_PANEL, maxHeight: pos.maxHeight, overflowY: 'auto',
      }}
      onClick={e => e.stopPropagation()}
    >
      {isMobile && (
        <div style={{ width: 40, height: 4, borderRadius: 99, background: 'var(--border-strong)', margin: '0 auto 14px' }} />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>Elegir color</span>
        <button type="button" onClick={() => setAbierto(false)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, lineHeight: 1, color: 'var(--text-muted)', padding: 4 }}
          aria-label="Cerrar">×</button>
      </div>

      {FAMILIAS.map(f => (
        <div key={f.nombre} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>{f.nombre}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: isMobile ? 8 : 6 }}>
            {f.tonos.map(c => (
              <Muestra key={c} color={c} tam={isMobile ? 36 : 30}
                activo={actual === c.toLowerCase()} onClick={() => elegir(c)} />
            ))}
          </div>
        </div>
      ))}

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
          Cualquier otro color
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="color"
            value={actual || '#25d366'}
            onChange={e => { setHex(e.target.value); onChange(e.target.value) }}
            style={{
              width: isMobile ? 52 : 44, height: isMobile ? 44 : 38, padding: 2, cursor: 'pointer',
              border: '1px solid var(--border)', borderRadius: 9, background: 'var(--bg-card)', flexShrink: 0,
            }}
            aria-label="Selector de color libre"
          />
          <input
            className="input"
            value={hex}
            onChange={e => {
              setHex(e.target.value)
              const n = normalizarHex(e.target.value)
              if (n) onChange(n)
            }}
            placeholder="#25D366"
            spellCheck={false}
            style={{ flex: 1, minWidth: 0, fontFamily: 'monospace', textTransform: 'lowercase' }}
            aria-label="Código hexadecimal"
          />
        </div>
      </div>
    </div>
  )

  return (
    <div ref={contenedor} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {visibles.map(c => (
          <Muestra key={c} color={c} tam={isMobile ? 38 : 34}
            activo={actual === c.toLowerCase()} onClick={() => onChange(c)} />
        ))}

        <button
          type="button"
          ref={disparador}
          onClick={alternar}
          title="Más colores"
          aria-label="Más colores"
          aria-expanded={abierto}
          style={{
            width: isMobile ? 38 : 34, height: isMobile ? 38 : 34, borderRadius: 9, cursor: 'pointer',
            border: '1px dashed var(--border-strong)', background: 'var(--bg-hover)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0,
            // Rueda de color en miniatura: se entiende sin leer nada
            backgroundImage: 'conic-gradient(#ef4444,#f97316,#eab308,#22c55e,#0ea5e9,#8b5cf6,#ec4899,#ef4444)',
          }}
        >
          <span style={{
            width: '58%', height: '58%', borderRadius: '50%', background: 'var(--bg-card)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, fontWeight: 700, color: 'var(--text-soft)', lineHeight: 1,
          }}>+</span>
        </button>
      </div>

      {abierto && isMobile && createPortal(
        <>
          <div
            onClick={() => setAbierto(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000 }}
          />
          {panel}
        </>,
        document.body,
      )}
      {abierto && !isMobile && createPortal(panel, document.body)}
    </div>
  )
}
